import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ProductContext } from '../../ProductProvider';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Helmet } from 'react-helmet-async';
import Barcode from 'react-barcode';
import './MyProfile.css';

const config = require('../../config.json');

export default function MyProfile() {
  const { validateLogin, loggedUser, myProfileImage } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();
  const [activeLoansCount, setActiveLoansCount] = useState(0);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      fetchUserLoans();
    }
  }, [loggedUser]);

  const fetchUserLoans = async () => {
    if (!loggedUser.nickname) return;
    try {
      setLoadingLoans(true);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const searchValue = loggedUser.nickname;
      const clientID = "VJCollege";
      const tempTopic = "UserID";

      const response = await axios.get(
        `${config.api.invokeURL}/vjlibrary/issue?fetchKey=${searchValue}&fetchOption=${tempTopic}&clientID=${clientID}`,
        {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setActiveLoansCount(response.data.length);
      }
    } catch (error) {
      console.warn("Unable to fetch active loans count:", error.message);
    } finally {
      setLoadingLoans(false);
    }
  };

  const handleCopyRegNo = (e) => {
    e.stopPropagation();
    if (!loggedUser.nickname) return;
    navigator.clipboard.writeText(loggedUser.nickname);
    setCopied(true);
    enqueueSnackbar(`Registration No "${loggedUser.nickname}" copied to clipboard!`, {
      variant: 'info',
      autoHideDuration: 2500
    });
    setTimeout(() => setCopied(false), 3000);
  };

  const handleImageError = (e) => {
    e.target.src = "https://s3.ap-south-1.amazonaws.com/idntica.com/avatar/profileImage/defaultAvatar.jpeg";
  };

  // Determine borrowing privileges & academic tiers based on user profile and stream
  const getBorrowerPrivileges = (profile, stream) => {
    const p = (profile || '').toLowerCase();
    const s = (stream || '').toUpperCase();

    if (p.includes('librarian') || p.includes('admin')) {
      return {
        roleLabel: 'Library Administrator',
        tierName: 'Staff & Administration',
        maxBooks: 50,
        loanDuration: '90 Days',
        renewalsAllowed: 'Unlimited',
        badgeBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
        badgeIcon: 'fa-user-shield'
      };
    }

    if (p.includes('faculty') || p.includes('professor') || p.includes('teacher')) {
      return {
        roleLabel: 'Faculty Member',
        tierName: 'Academic Faculty',
        maxBooks: 50,
        loanDuration: '90 Days',
        renewalsAllowed: '3 Times',
        badgeBg: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
        badgeIcon: 'fa-chalkboard-teacher'
      };
    }

    if (s.includes('DTH') || p.includes('doctoral')) {
      return {
        roleLabel: 'Doctoral Scholar (D.Th)',
        tierName: 'Doctoral Research',
        maxBooks: 50,
        loanDuration: '60 Days',
        renewalsAllowed: '2 Times',
        badgeBg: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        badgeIcon: 'fa-graduation-cap'
      };
    }

    if (s.includes('LTH') || p.includes('licentiate')) {
      return {
        roleLabel: 'Licentiate Scholar (L.Th)',
        tierName: 'Licentiate Program',
        maxBooks: 30,
        loanDuration: '40 Days',
        renewalsAllowed: '2 Times',
        badgeBg: 'linear-gradient(135deg, #0284c7, #0369a1)',
        badgeIcon: 'fa-user-graduate'
      };
    }

    if (s.includes('BTH') || p.includes('student')) {
      return {
        roleLabel: 'Theology Student (B.Th)',
        tierName: 'Undergraduate Program',
        maxBooks: 20,
        loanDuration: '30 Days',
        renewalsAllowed: '2 Times',
        badgeBg: 'linear-gradient(135deg, #10b981, #047857)',
        badgeIcon: 'fa-book-reader'
      };
    }

    return {
      roleLabel: loggedUser.profile || 'Library Member',
      tierName: 'Academic Member',
      maxBooks: 20,
      loanDuration: '30 Days',
      renewalsAllowed: '2 Times',
      badgeBg: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
      badgeIcon: 'fa-id-card'
    };
  };

  const privileges = getBorrowerPrivileges(loggedUser.profile, loggedUser.family_name);
  const remainingQuota = Math.max(0, privileges.maxBooks - activeLoansCount);
  const quotaPercent = Math.min(100, Math.round((activeLoansCount / privileges.maxBooks) * 100));

  const isStaff = loggedUser.isAdmin ||
    loggedUser.profile === 'Admin' ||
    loggedUser.profile === 'Super Admin' ||
    loggedUser.profile === 'Librarian' ||
    loggedUser.profile === 'Non-Teaching Staff';

  return (
    <div className="myprofile-container">
      <Helmet>
        <title>My Profile - Digital Identity | Vidyajyoti College of Theology Library</title>
        <meta name="description" content="Digital Library Identity and Academic Profile at Vidyajyoti College of Theology, Delhi." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <div className="myprofile-wrapper">
        {/* Navigation Breadcrumbs & Print Action */}
        <div className="profile-nav-header">
          <div className="profile-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i> Home
            </Link>
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>My Library Profile</span>
          </div>

          <div className="profile-header-actions">
            <button
              type="button"
              className="btn-profile-header-action"
              onClick={() => window.print()}
              title="Print Library Card / Profile Summary"
            >
              <i className="fa fa-print"></i> Print Digital Pass
            </button>
            <Link to="/mybooks" className="btn-profile-header-action">
              <i className="fa fa-book"></i> Active Loans ({activeLoansCount})
            </Link>
          </div>
        </div>

        {/* =========================================================
            Digital Library Identity Smart Card (Hero Section)
            ========================================================= */}
        <div className="library-id-card">
          <div className="library-id-watermark">
            <i className="fa fa-university"></i>
          </div>

          <div className="library-id-header">
            <div className="library-id-institution">
              <div className="library-id-crest">
                <i className="fa fa-book-open"></i>
              </div>
              <div>
                <div className="library-id-college">Vidyajyoti College of Theology</div>
                <div className="library-id-college-sub">Delhi • Institute of Religious Studies</div>
              </div>
            </div>

            <div className="library-id-type-badge" style={{ background: privileges.badgeBg }}>
              <i className={`fa ${privileges.badgeIcon}`}></i>
              <span>{privileges.tierName}</span>
            </div>
          </div>

          <div className="library-id-body">
            <div className="library-id-user-section">
              <div className="library-id-avatar-wrapper">
                <img
                  id="profile"
                  src={myProfileImage}
                  onError={handleImageError}
                  alt={loggedUser.name || "Member Avatar"}
                  className="library-id-avatar"
                />
                <span className="library-id-status-dot" title="Active Member • In Good Standing"></span>
              </div>

              <div className="library-id-info">
                <div className="library-id-name">{loggedUser.name || "Library Member"}</div>
                <div className="library-id-program">
                  <i className="fa fa-graduation-cap"></i>
                  <span>
                    {loggedUser.family_name
                      ? `${loggedUser.family_name} • ${privileges.roleLabel}`
                      : privileges.roleLabel}
                  </span>
                </div>

                <div className="library-id-meta-pills">
                  <div
                    className="library-id-number-pill"
                    onClick={handleCopyRegNo}
                    title="Click to copy Registration Number"
                  >
                    <i className="fa fa-id-badge"></i>
                    <span>{loggedUser.nickname || "NO-REG-ID"}</span>
                    <i className={`fa ${copied ? 'fa-check text-success' : 'fa-copy copy-icon'}`}></i>
                  </div>
                </div>
              </div>
            </div>

            {loggedUser.nickname && (
              <div className="library-id-barcode-box" title="Scan at Library Circulation Desk">
                <Barcode
                  value={loggedUser.nickname}
                  width={1.25}
                  height={42}
                  fontSize={11}
                  margin={2}
                  displayValue={false}
                />
                <span className="library-id-barcode-label">Desk Circulation Barcode</span>
              </div>
            )}
          </div>

          <div className="library-id-footer">
            <div className="library-id-chip-tag">
              <i className="fa fa-microchip"></i>
              <span>Digital Library Smart Pass • AY 2024–2026</span>
            </div>
            <div>
              <span>Status: </span>
              <strong style={{ color: '#86efac' }}>Active & Verified</strong>
            </div>
          </div>
        </div>

        {/* =========================================================
            Borrower Privileges & Quota Metric KPI Strip
            ========================================================= */}
        <div className="profile-kpi-grid">
          {/* Quota Limit */}
          <div className="profile-kpi-card">
            <div>
              <div className="profile-kpi-header">
                <div className="profile-kpi-label">
                  <i className="fa fa-layer-group text-primary"></i>
                  <span>Borrowing Quota</span>
                </div>
                <div className="profile-kpi-icon-pill" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                  <i className="fa fa-book"></i>
                </div>
              </div>
              <div className="profile-kpi-value">{privileges.maxBooks} <span style={{ fontSize: '1rem', color: '#64748b' }}>Books</span></div>
            </div>
            <div className="profile-kpi-subtext">Maximum concurrent allocation</div>
          </div>

          {/* Active Loans */}
          <div className="profile-kpi-card">
            <div>
              <div className="profile-kpi-header">
                <div className="profile-kpi-label">
                  <i className="fa fa-bookmark text-warning"></i>
                  <span>Active Loans</span>
                </div>
                <div className="profile-kpi-icon-pill" style={{ background: '#fef3c7', color: '#d97706' }}>
                  <i className="fa fa-hand-holding"></i>
                </div>
              </div>
              <div className="profile-kpi-value">
                {loadingLoans ? '...' : activeLoansCount}{' '}
                <span style={{ fontSize: '1rem', color: '#64748b' }}>Books</span>
              </div>
            </div>
            <div>
              <div className="profile-kpi-subtext">Currently checked out</div>
              <div className="profile-kpi-progress">
                <div
                  className="profile-kpi-progress-bar"
                  style={{
                    width: `${quotaPercent}%`,
                    background: quotaPercent > 80 ? '#ef4444' : quotaPercent > 50 ? '#f59e0b' : '#10b981'
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Available Balance */}
          <div className="profile-kpi-card">
            <div>
              <div className="profile-kpi-header">
                <div className="profile-kpi-label">
                  <i className="fa fa-check-circle text-success"></i>
                  <span>Available Quota</span>
                </div>
                <div className="profile-kpi-icon-pill" style={{ background: '#dcfce7', color: '#15803d' }}>
                  <i className="fa fa-plus-circle"></i>
                </div>
              </div>
              <div className="profile-kpi-value" style={{ color: '#059669' }}>
                {loadingLoans ? '...' : remainingQuota}{' '}
                <span style={{ fontSize: '1rem', color: '#64748b' }}>Remaining</span>
              </div>
            </div>
            <div className="profile-kpi-subtext">Ready for checkout</div>
          </div>

          {/* Loan Duration */}
          <div className="profile-kpi-card">
            <div>
              <div className="profile-kpi-header">
                <div className="profile-kpi-label">
                  <i className="fa fa-calendar-alt text-info"></i>
                  <span>Standard Loan</span>
                </div>
                <div className="profile-kpi-icon-pill" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
                  <i className="fa fa-clock"></i>
                </div>
              </div>
              <div className="profile-kpi-value">{privileges.loanDuration}</div>
            </div>
            <div className="profile-kpi-subtext">{privileges.renewalsAllowed} online renewals</div>
          </div>
        </div>

        {/* =========================================================
            Detailed Information Grid Panels
            ========================================================= */}
        <div className="profile-details-grid">
          {/* Panel 1: Institutional & Academic Identity */}
          <div className="profile-details-card">
            <div className="profile-section-title">
              <div className="profile-section-title-left">
                <div className="profile-section-title-icon">
                  <i className="fa fa-university"></i>
                </div>
                <span>Institutional Credentials</span>
              </div>
              <span className="badge bg-light text-primary border">Academic Record</span>
            </div>

            <div className="profile-field-list">
              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-id-card"></i> Member Registration ID
                  </span>
                  <span className="profile-field-value" style={{ fontFamily: 'monospace', color: '#0284c7' }}>
                    {loggedUser.nickname || 'Not Assigned'}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCopyRegNo}
                  title="Copy Registration Number"
                >
                  <i className="fa fa-copy"></i>
                </button>
              </div>

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-user-tag"></i> Institutional Role
                  </span>
                  <span className="profile-field-value">{loggedUser.profile || 'Student'}</span>
                </div>
                <span className="profile-field-badge bg-primary-subtle text-primary">
                  {privileges.tierName}
                </span>
              </div>

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-graduation-cap"></i> Academic Program / Department
                  </span>
                  <span className="profile-field-value">
                    {loggedUser.family_name || 'Vidyajyoti Theological Faculty'}
                  </span>
                </div>
              </div>

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-building"></i> College / Institution
                  </span>
                  <span className="profile-field-value">
                    {loggedUser.website || 'Vidyajyoti College of Theology, Delhi'}
                  </span>
                </div>
              </div>

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-shield-alt"></i> Library Clearance Status
                  </span>
                  <span className="profile-field-value" style={{ color: '#059669' }}>
                    Good Standing (No Overdue Fines)
                  </span>
                </div>
                <span className="profile-field-badge bg-success-subtle text-success">
                  <i className="fa fa-check-circle"></i> Active
                </span>
              </div>
            </div>
          </div>

          {/* Panel 2: Personal & Contact Information */}
          <div className="profile-details-card">
            <div className="profile-section-title">
              <div className="profile-section-title-left">
                <div className="profile-section-title-icon">
                  <i className="fa fa-user-circle"></i>
                </div>
                <span>Account & Contact Information</span>
              </div>
              <span className="badge bg-light text-success border">Verified Profile</span>
            </div>

            <div className="profile-field-list">
              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-envelope"></i> Institutional Email Address
                  </span>
                  <span className="profile-field-value">{loggedUser.email || 'Not Available'}</span>
                </div>
                <span className="profile-field-badge bg-info-subtle text-info">
                  <i className="fa fa-check"></i> Verified
                </span>
              </div>

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-phone"></i> Contact Phone Number
                  </span>
                  <span className="profile-field-value">
                    {loggedUser.phone_number || 'Not Provided'}
                  </span>
                </div>
              </div>

              {loggedUser.gender && (
                <div className="profile-field-item">
                  <div className="profile-field-meta">
                    <span className="profile-field-label">
                      <i className="fa fa-venus-mars"></i> Gender
                    </span>
                    <span className="profile-field-value">{loggedUser.gender}</span>
                  </div>
                </div>
              )}

              {loggedUser.DOB && (
                <div className="profile-field-item">
                  <div className="profile-field-meta">
                    <span className="profile-field-label">
                      <i className="fa fa-birthday-cake"></i> Date of Birth
                    </span>
                    <span className="profile-field-value">{loggedUser.DOB}</span>
                  </div>
                </div>
              )}

              <div className="profile-field-item">
                <div className="profile-field-meta">
                  <span className="profile-field-label">
                    <i className="fa fa-key"></i> System Access Rights
                  </span>
                  <span className="profile-field-value">
                    {isStaff ? 'Circulation, Cataloguing & Administration' : 'General Borrowing & Research Archive'}
                  </span>
                </div>
                <span className={`profile-field-badge ${isStaff ? 'bg-danger-subtle text-danger' : 'bg-primary-subtle text-primary'}`}>
                  {isStaff ? 'Staff Access' : 'Student Access'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================
            Library Privileges & Services Highlight Banner
            ========================================================= */}
        <div className="profile-policy-card">
          <div className="profile-policy-header">
            <i className="fa fa-info-circle"></i>
            <span>Library Circulation Policies & Privileges</span>
          </div>

          <div className="profile-policy-grid">
            <div className="profile-policy-item">
              <i className="fa fa-book-reader profile-policy-icon"></i>
              <div className="profile-policy-text">
                <strong>Borrowing & Renewal Policy</strong>
                Books can be renewed up to {privileges.renewalsAllowed} prior to the due date if no other holds are active.
              </div>
            </div>

            <div className="profile-policy-item">
              <i className="fa fa-newspaper profile-policy-icon"></i>
              <div className="profile-policy-text">
                <strong>Theological Journals & Serials</strong>
                Unrestricted digital access to all Vidyajyoti Journal of Theological Reflection (VJTR) archives and bound volumes.
              </div>
            </div>

            <div className="profile-policy-item">
              <i className="fa fa-barcode profile-policy-icon"></i>
              <div className="profile-policy-text">
                <strong>Instant Barcode Checkouts</strong>
                Present your Digital Library Smart Pass or Registration barcode at the circulation desk for fast automated issue.
              </div>
            </div>

            <div className="profile-policy-item">
              <i className="fa fa-coins profile-policy-icon"></i>
              <div className="profile-policy-text">
                <strong>Overdue Policy & Fines</strong>
                Grace period of 2 days is provided. Standard nominal fine applies on overdue books to ensure equal resource availability.
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================
            Quick Actions Hub
            ========================================================= */}
        <div className="profile-actions-card">
          <div className="profile-actions-title">
            <i className="fa fa-compass text-primary"></i>
            <span>Quick Navigation Hub</span>
          </div>

          <div className="profile-actions-buttons">
            <Link to="/mybooks" className="btn-profile-primary">
              <i className="fa fa-book-open"></i>
              <span>View My Borrowed Books</span>
              <span className="badge bg-white text-dark ms-1">{activeLoansCount}</span>
            </Link>

            <Link to="/search" className="btn-profile-secondary">
              <i className="fa fa-search"></i>
              <span>Search Library Catalogue</span>
            </Link>

            <Link to="/categories" className="btn-profile-secondary">
              <i className="fa fa-th-large"></i>
              <span>Explore Book Categories</span>
            </Link>

            {/* Librarian / Staff Specific Shortcuts */}
            {isStaff && (
              <>
                <Link to="/issuereturnbooks" className="btn-profile-admin">
                  <i className="fa fa-exchange-alt"></i>
                  <span>Circulation Desk</span>
                </Link>

                <Link to="/managebooks" className="btn-profile-admin">
                  <i className="fa fa-plus-circle"></i>
                  <span>Manage Books</span>
                </Link>

                <Link to="/managejournal" className="btn-profile-admin">
                  <i className="fa fa-newspaper"></i>
                  <span>Manage Journals</span>
                </Link>

                <Link to="/reports" className="btn-profile-secondary">
                  <i className="fa fa-chart-bar"></i>
                  <span>Library Reports</span>
                </Link>

                <Link to="/barcode" className="btn-profile-secondary">
                  <i className="fa fa-barcode"></i>
                  <span>Barcode Center</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
